/* @flow */
import { Observable } from 'rxjs';
import { stringify } from 'querystring';
import { combineEpics } from 'redux-observable';
import { change, unregisterField } from 'redux-form';
import { path } from 'ramda';
import { List, Map, fromJS } from 'immutable';
import { saveTextToFile, alphabetize } from 'trustar/utils';
import createDebugger from 'debug';
import { prop } from 'ramda';

import { sanitizeCsvFields } from '../../utils/csv';

const debug = createDebugger('app:modules:users:epics'); // eslint-disable-line no-unused-vars

import {
  FETCH,
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILURE,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  DEACTIVATE,
  DEACTIVATE_SUCCESS,
  DEACTIVATE_FAILURE,
  EXPORT_ALL,
  EXPORT_ALL_FAILURE,
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
} from './action-types.js';
import { fetch } from './actions.js';
import { getQueryParams } from 'trustar/utils';
import { displayOptions as getOptions } from './getters.js';
import { success } from '../alerts/actions';
import { User } from './types.js';

import { determineAccessType } from 'modules/enclave/utils';

const url = (subpath: ?string): string => {
  const base = '/services/users';
  return subpath ? `${base}/${subpath}` : base;
};

const toJSON = (user) => {
  if (!(user instanceof User) || !Map.isMap(user)) {
    debug('toJSON was called on a non-immutable object.');
    user = fromJS(user);
  }

  return user
    .update('email', v => v ? v.trim() : v)
    .update('username', v => v ? v.trim() : v)
    .update('enclaves', e => e.toList())
    .update('company', company =>
      company ? company
        .delete('enclaves')
        .delete('sector') : null
      )
    .update('role', role =>
      role
        ? { name: role.get('name') }
        : null
    )
    .toJS();
};

const fetchUserEpic = (action$, store, { ajax }) =>
    action$.ofType(FETCH_USER)
        .mergeMap(({ userId }) =>
            ajax.get(url(userId))
                .map(({ response }) => ({
                    type: FETCH_USER_SUCCESS,
                    payload: response,
                }))
                .catch(err => Observable.of({
                    type: FETCH_USER_FAILURE,
                    payload: err,
                })));

const fetchEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH)
    .map(prop('payload'))
    .debounceTime(500)
    .switchMap(params => {
      const parameterizedUrl = url() + '?' + stringify(params);
      return ajax.get(parameterizedUrl)
        .map(({ response }) => ({
          type: FETCH_SUCCESS,
          payload: response,
          meta: {
            startPage: params.startPage,
            hasNext: response.length == params.pageSize,
          },
        }))
        .catch(err => Observable.of({
          type: FETCH_FAILURE,
          payload: err,
        }));
    });

const saveUserFailure = (err: Error, msg: ?string) => ({
  type: SAVE_FAILURE,
  error: true,
  payload: err,
  meta: {
    alert: { type: 'error', text: msg ? msg : 'Could not save user.' },
  },
});

/**
 * NOTE: This epic takes care of both save and update, which both have different
 * URLs. The url will be determined by the presence of a GUID on the object to
 * be saved.
 */
const saveEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE)
    .mergeMap(({ payload }) => {
      if (!payload) {
        return Observable.of(saveUserFailure(new Error('Payload was undefined')));
      }

      const id = payload.get('guid');
      const data = toJSON(payload);

      return ajax.postJSON(url(id), data)
        .map(({ response }) => ({
          type: SAVE_SUCCESS,
          payload: response,
          meta: {
            alert: { type: 'success', text: 'User saved.' },
          },
        }))
        .catch(err => {
          const message = path(['xhr', 'response', 'message'], err);
          return Observable.of(saveUserFailure(err, message));
        });
    });

/**
 * Trigger a re-fetch of users with the currently specified options. For
 * example, when a new user is saved rather than tack it on to state we need to
 * refetch. The reason is, all the logic related to sorting and filtering lives
 * on the server side, so we need to the server to provide us with an updated
 * list of users based on our current criteria.
 */
const refetchEpic = (action$, store) =>
  action$.ofType(SAVE_SUCCESS, DEACTIVATE_SUCCESS, RESET_PASSWORD_SUCCESS)
    .map(() => getOptions(store.getState()))
    .map(getQueryParams)
    .map(fetch);

const deleteEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE)
    .mergeMap(({ payload: id }) =>
      ajax.delete(url(id))
        .map(() => ({
          type: DELETE_SUCCESS,
          payload: id,
          meta: {
            alert: { type: 'success', text: 'User removed.' },
          },
        }))
        .catch(err => Observable.of({
          type: DELETE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: { type: 'error', text: 'Could not delete user.' },
          },
        })));

const deactivateEpic = (action$, store, { ajax }) =>
  action$.ofType(DEACTIVATE)
    .mergeMap(({ payload }) => {
      const { email, deactivated } = payload;
      return ajax.postForm(url('deactivate'), payload)
        .map(({ response }) => ({
          type: DEACTIVATE_SUCCESS,
          payload: response,
          meta: {
            alert: {
              type: 'success',
              text: `User ${response.deactivated ? 'de' : 're'}activated.`,
            },
          },
        }))
        .catch(err => Observable.of({
          type: DEACTIVATE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: {
              type: 'error',
              text: `Could not ${deactivated ? 'de' : 're'}activate user.`
            },
          },
        }));
    });

const resetPasswordFailure = (err: Error) => ({
  type: RESET_PASSWORD_FAILURE,
  payload: err,
  error: true,
  meta: {
    alert: { type: 'error', text: 'Reset password failed.' },
  },
});

const resetPasswordEpic = (action$, store, { ajax }) =>
  action$.ofType(RESET_PASSWORD)
    .mergeMap(({ payload: user }) => {
      if (!user) {
        return Observable.of(resetPasswordFailure(new Error('Payload was undefined')));
      }

      const id = user.get('guid');
      const successMessage = user.get('activationDate')
        ? 'Password reset.'
        : 'Confirmation sent';

      return ajax.post(`/services/password/send-reset/${id}`)
        .map(({ response }) => ({
          type: RESET_PASSWORD_SUCCESS,
          payload: response,
          meta: {
            alert: { type: 'success', text: successMessage },
          },
        }))
        .catch(err => Observable.of(resetPasswordFailure(err)));
    });

const exportAllEpic = (action$, store, { ajax }) =>
  action$.ofType(EXPORT_ALL)
    .mergeMap(() =>
      // <-- IMPORTANT. response will be null if the observable doesn't know its responseType text
      ajax.get(url('users.csv'), {}, { responseType: 'text'})
        .do((response) => {
          if (response.response != 'email') {
            const sanitizedCsv = sanitizeCsvFields(response.response);
            saveTextToFile('UserDirectory.csv', sanitizedCsv);
          } else {
            store.dispatch(success('An e-mail with the user list will be delivered shortly'));
          }
        })
        .ignoreElements()
        .catch(err => Observable.of({
          type: EXPORT_ALL_FAILURE,
          payload: err,
          error: true,
          meta: {
            alert: { type: 'error', text: 'Could not export user directory.' },
          },
        })));

/* Manually interact with Redux-Form actions and store when the user
 * updates the company selection. We need to populate the enclave form
 * fields based on the selected companies enclave. We also need to
 * update the MFA and SSO fields according to the company selection/deselection.
*/
const selectCompanyUsersFormEpic = (action$, store) =>
  action$.ofType('@@redux-form/CHANGE')
    .filter(({ meta: { field, form }}) => form === 'user' && field === 'company')
    .mergeMap(({ payload: company }) => Observable.defer(() => {
      const setCompanyEnclavesAction = change(
        'user',
        'enclaves',
        company ? company.get('enclaves')
          .sort(alphabetize)
          .toOrderedMap()
          .map(e => e.set('maximumAccess', determineAccessType(e)))
          .mapKeys((k, v) => v.get('id')) : List()
      );
      const actions = company ? [
        change('user', 'ssoEnabled', false),
        change('user', 'mfaEnabled', false),
      ] : [
        unregisterField('user', 'ssoEnabled'),
        unregisterField('user', 'mfaEnabled'),
      ];
      return Observable.from([ setCompanyEnclavesAction, ...actions ]);
    }).catch(e => Observable.empty()));

export default combineEpics(
  fetchEpic,
  fetchUserEpic,
  saveEpic,
  deleteEpic,
  deactivateEpic,
  resetPasswordEpic,
  exportAllEpic,
  selectCompanyUsersFormEpic,
  refetchEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/users/epics.js